import React, { useState, useRef, useCallback } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import cn from 'classnames';

import backIcon from '../../assets/images/my-meetings/back.svg';
import EditableTitle from '../Library/EditableTitle';

import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import classes from './styles.module.scss';

export default function Breadcrumbs({
  breadcrumbsOptions,
  className,
  showBackButton,
  editableLastItem,
}) {
  const navigate = useNavigate();
  const isMobile = useCheckMobileScreen();
  const [isEllipsisOpen, setIsEllipsisOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const ellipsisRef = useRef(null);
  useOnClickOutside(ellipsisRef, () => setIsEllipsisOpen(false));

  const handleEditableTitleUpdate = useCallback(
    async (newTitle) => {
      if (!editableLastItem) return;
      try {
        await editableLastItem.onUpdate(newTitle);
        setIsEditingTitle(false);
      } catch {
        setIsEditingTitle(false);
      }
    },
    [editableLastItem]
  );

  let content;

  if (showBackButton) {
    content = (
      <button
        onClick={() => navigate(-1)}
        type="button"
        className={classes.backButton}
      >
        <img src={backIcon} alt="Back" />
        <span>Go back</span>
      </button>
    );
  } else if (isMobile) {
    const prevOption = breadcrumbsOptions[breadcrumbsOptions.length - 2];
    const currentOption = breadcrumbsOptions[breadcrumbsOptions.length - 1];

    content = (
      <Link
        to={prevOption?.to}
        className={cn(classes.link, prevOption && classes.linkWithPrev)}
      >
        {currentOption?.label}
      </Link>
    );
  } else {
    const opts = breadcrumbsOptions || [];
    const shouldCollapse =
      opts.length >= 4 &&
      opts[0]?.label === 'My Library' &&
      opts[0]?.to === '/my-library';

    if (shouldCollapse) {
      const first = opts[0];
      const hiddenParents = opts.slice(1, -2);
      const parent = opts[opts.length - 2];
      const current = opts[opts.length - 1];

      content = (
        <>
          <Link to={first.to} className={classes.link}>
            {first.label}
          </Link>
          <span className={classes.ellipsisWrapper} ref={ellipsisRef}>
            <button
              type="button"
              className={classes.ellipsisButton}
              onClick={() => setIsEllipsisOpen((prev) => !prev)}
              aria-expanded={isEllipsisOpen}
              aria-haspopup="true"
            >
              ...
            </button>
            <span className={classes.separator}>&gt;</span>
            {isEllipsisOpen && hiddenParents.length > 0 && (
              <ul className={classes.ellipsisDropdown} role="menu">
                {[...hiddenParents].reverse().map((option) => (
                  <li key={option.label} role="none">
                    <Link
                      to={option.to}
                      className={classes.ellipsisDropdownLink}
                      role="menuitem"
                      onClick={() => setIsEllipsisOpen(false)}
                    >
                      {option.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </span>
          <Link to={parent.to} className={classes.link}>
            {parent.label}
          </Link>
          {editableLastItem?.canEdit && current.to === '#' ? (
            <EditableTitle
              handleUpdateTitle={handleEditableTitleUpdate}
              isEditable
              isEditing={isEditingTitle}
              setIsEditing={setIsEditingTitle}
              className={classes.editableTitle}
              clickTitleToEdit
            >
              {current.label}
            </EditableTitle>
          ) : (
            <Link to={current.to} className={classes.link}>
              {current.label}
            </Link>
          )}
        </>
      );
    } else {
      content = opts.map((option, index) => {
        const isLast = index === opts.length - 1;
        const useEditable =
          isLast &&
          editableLastItem?.canEdit &&
          option.to === '#';

        if (useEditable) {
          return (
            <EditableTitle
              key={option.label}
              handleUpdateTitle={handleEditableTitleUpdate}
              isEditable
              isEditing={isEditingTitle}
              setIsEditing={setIsEditingTitle}
              className={classes.editableTitle}
              clickTitleToEdit
            >
              {option.label}
            </EditableTitle>
          );
        }

        return (
          <Link key={option.label} to={option.to} className={classes.link}>
            {option.label}
          </Link>
        );
      });
    }
  }

  return (
    <div
      className={cn(classes.Breadcrumbs, 'Breadcrumbs', className, {
        [classes.hasOptions]: breadcrumbsOptions?.length > 1,
      })}
    >
      {content}
    </div>
  );
}
